import { useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { useDispatch } from "react-redux";
import { getInstance, getTenants } from "../../../../store/instanceManagement/instanceService";
import { saveInstances } from "../../../../store/slices/instancesSlice";
import { saveTenantsList } from "../../../../store/slices/tenantsSlice";
import { setInstanceCultureName, setInstanceTimezone } from "../../../../store/slices/authSlice";
import { authService } from "../../../../utils/authService";
import { logger } from "../../../../services/logger.service";

interface SkyPointLinkConfiguration {
  DocumentLink?: string;
  IdeaLink?: string;
  PrivacyStatementLink?: string;
  [key: string]: string | undefined;
}

interface HeaderDataState {
  selectedInstanceId: string;
  selectedInstanceName: string;
  selectedTenantBusinessName: string;
  skyPointLinkConfiguration: SkyPointLinkConfiguration;
  isInstanceLoaded: boolean;
  isTenantLoaded: boolean;
}

interface UseHeaderDataReturn extends HeaderDataState {
  refreshTenantData: () => void;
  refreshInstanceData: () => void;
}

export function useHeaderData(): UseHeaderDataReturn {
  const dispatch = useDispatch();
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>("");
  const [selectedInstanceName, setSelectedInstanceName] = useState<string>("");
  const [selectedTenantBusinessName, setSelectedTenantBusinessName] = useState<string>("");
  const [skyPointLinkConfiguration, setSkyPointLinkConfiguration] =
    useState<SkyPointLinkConfiguration>({});
  const [isInstanceLoaded, setIsInstanceLoaded] = useState<boolean>(false);
  const [isTenantLoaded, setIsTenantLoaded] = useState<boolean>(false);

  const fetchTenantData = useCallback(() => {
    getTenants()
      .then((data) => {
        data.tenants.sort((a: { businessname: string }, b: { businessname: string }) =>
          a.businessname.toLowerCase().localeCompare(b.businessname.toLowerCase()),
        );

        // A saved tenant preference may only be honoured when it owns the
        // instance we resolved. getTenants() derives selectedTenant from that
        // instance, so anything else here is a leftover from a previous session
        // and applying it is what rendered "Skypoint Production" above an
        // instance belonging to the Senior Living demo tenant.
        const savedTenantId = authService.getSavedTenantId();

        if (savedTenantId && savedTenantId !== data.selectedTenant?.id) {
          authService.saveTenantId(data.selectedTenant?.id || "");
        }

        dispatch(saveTenantsList(data));
        setIsTenantLoaded(true);
      })
      .catch((error) => {
        logger.error("Failed to get tenants", error);
        setIsTenantLoaded(true);
      });
  }, [dispatch]);

  const fetchInstanceData = useCallback(() => {
    getInstance()
      .then((data) => {
        dispatch(setInstanceCultureName(data?.instanceCultureName ?? "en-US"));
        // No default: an invented zone is worse than none (instanceService).
        dispatch(setInstanceTimezone(data?.instanceDefault.timezone));

        data.instances.sort((a: { displayname: string }, b: { displayname: string }) =>
          a.displayname.toLowerCase().localeCompare(b.displayname.toLowerCase()),
        );

        // Check localStorage for the saved instance ID
        const savedInstanceId = authService.getSavedInstanceId();
        const backendTenantId = data.tenant?.id || "";

        let finalInstanceId = data.selectedInstanceId;
        let finalInstanceName = data.selectedInstanceName;

        // If there's a saved instance ID, check if it exists in the fetched instances
        if (savedInstanceId && data.instances) {
          const savedInstance = data.instances.find(
            (inst: { id: string; displayname: string }) => inst.id === savedInstanceId,
          );
          if (savedInstance) {
            finalInstanceId = savedInstance.id;
            finalInstanceName = savedInstance.displayname;
          }
        }

        // Update data with the resolved instance before saving to Redux
        // This ensures Redux state reflects the localStorage preference
        data.selectedInstanceId = finalInstanceId;
        data.selectedInstanceName = finalInstanceName;

        dispatch(saveInstances(data));

        // Always write the pair we actually resolved. The old guard skipped the
        // write whenever the saved tenant disagreed with the backend — which is
        // exactly the broken state — so a mismatched pair could never repair
        // itself and survived every reload. data.instances is scoped to this
        // tenant, so finalInstanceId is guaranteed to belong to backendTenantId.
        authService.saveInstanceId(finalInstanceId);
        authService.saveTenantId(backendTenantId);

        flushSync(() => {
          setSelectedInstanceId(finalInstanceId);
          setSelectedInstanceName(finalInstanceName);
          setSelectedTenantBusinessName(data.tenant.businessname);
          setSkyPointLinkConfiguration(data.skyPointLinkConfiguration || {});
          setIsInstanceLoaded(true);
        });

        // Store for analytics
        const instanceData = data?.instances.filter(
          (item: { id: string; name: string }) => item.id === finalInstanceId,
        );
        const instanceName = instanceData[0]?.name;
        const tenantName = data?.tenant?.name;
        if (tenantName && instanceName) {
          localStorage.setItem("Tenant_instance", `${tenantName.toLowerCase()}_${instanceName}`);
        }
      })
      .catch((error) => {
        logger.error("Failed to get instances", error);
        setIsInstanceLoaded(true);
      });
  }, [dispatch]);

  useEffect(() => {
    fetchTenantData();
    fetchInstanceData();
  }, [fetchTenantData, fetchInstanceData]);

  return {
    selectedInstanceId,
    selectedInstanceName,
    selectedTenantBusinessName,
    skyPointLinkConfiguration,
    isInstanceLoaded,
    isTenantLoaded,
    refreshTenantData: fetchTenantData,
    refreshInstanceData: fetchInstanceData,
  };
}
